// src/pages/PurchaseList.tsx
import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import EmptyState from "../components/EmptyState";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { PaperClipIcon } from "@heroicons/react/24/outline";
import { apiGet } from "../api/client";
import { companyRoute } from "../utils/routeHelpers";
import {
  ListToolbar,
  type MenuItem,
  type SortOption,
} from "../components/ListToolbar";

// shared sort helpers
import {
  SortKey as BaseSortKey,
  SortDir,
  compareDates,
  compareInvoiceNo,
} from "../utils/sortUtils";

// date-only safe display (prevents timezone shifting)
import {
  formatDateYMDToLocale,
  formatAnyDateToLocale,
} from "../utils/dateUtils";

import { A, pillClass } from "../styles/appleClasses";
import { Button } from "../components/Button";
import ListTableShell from "../components/lists/ListTableShell";
import { formatMoney } from "../utils/formatNumber";

interface Vendor {
  id: number;
  name: string;
  email?: string;
}

interface PurchaseLineLite {
  id: number;
  product_id: number;
  product_name: string;
}

interface PurchaseSourceDocument {
  id: number;
  original_filename?: string | null;
  content_type?: string | null;
  byte_size?: number | null;
  created_at: string;
}

interface Purchase {
  id: number;
  date: string;
  due_date?: string | null; // ✅ NEW
  invoice_id?: number | string;
  invoice_number?: string;
  vendor: Vendor | null;
  amount: number;
  status?: string;
  voided?: boolean;
  lines?: PurchaseLineLite[];
  source_document_id?: number | null;
  source_document?: PurchaseSourceDocument | null;
}

type PurchaseSortKey = BaseSortKey | "products" | "status";

function toISODateOnly(s: string): string {
  // supports "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm..."
  return (s || "").slice(0, 10);
}

function formatDate(dateStr?: string | null) {
  if (!dateStr) return "—";

  const ymd = toISODateOnly(String(dateStr));
  // Date-only safe display (local midnight, no UTC shift)
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return formatDateYMDToLocale(ymd);

  // Datetime fallback
  return formatAnyDateToLocale(String(dateStr));
}

function formatAmount(amount: number) {
  return amount != null ? formatMoney(amount) : "—";
}

function getProductNames(p: Purchase): string[] {
  const names = (p.lines ?? []).map((ln) => ln.product_name).filter(Boolean);
  return Array.from(new Set(names));
}

function formatProductSummary(p: Purchase): string {
  const names = getProductNames(p);
  if (names.length === 0) return "—";
  if (names.length <= 3) return names.join(", ");
  return `${names.slice(0, 3).join(", ")} +${names.length - 3}`;
}

function invoiceStr(p: Purchase): string | null {
  return (
    p.invoice_number ?? (p.invoice_id != null ? String(p.invoice_id) : null)
  );
}

function isOnAccount(p: Purchase): boolean {
  const inv = toISODateOnly(p.date || "");
  const due = toISODateOnly(String(p.due_date || ""));
  if (!inv || !due) return false;
  return due !== inv;
}

function dueDateDisplay(p: Purchase): string {
  if (!p.due_date) return "—";
  if (!isOnAccount(p)) return "—";
  return formatDate(p.due_date);
}

function compareText(a: string, b: string, dir: SortDir) {
  const aa = (a || "").toLowerCase();
  const bb = (b || "").toLowerCase();
  if (aa < bb) return dir === "asc" ? -1 : 1;
  if (aa > bb) return dir === "asc" ? 1 : -1;
  return 0;
}

function productsSortKey(p: Purchase): string {
  const names = getProductNames(p)
    .map((n) => String(n).toLowerCase().trim())
    .filter(Boolean)
    .sort();
  return names.join(", ");
}

// Apple-standard pills (LOCKED)
// Fixed-English export contract: never translate csvLabel.
const statusInfoByKey: Record<string, { csvLabel: string; className: string }> = {
  paid: { csvLabel: "Paid", className: pillClass("success") },

  partial: { csvLabel: "Partial Paid", className: pillClass("warning") },
  partially_paid: { csvLabel: "Partial Paid", className: pillClass("warning") },

  open: { csvLabel: "Unpaid", className: pillClass("warning") },
  unpaid: { csvLabel: "Unpaid", className: pillClass("warning") },
  overdue: { csvLabel: "Overdue", className: pillClass("warning") },

  voided: { csvLabel: "Voided", className: pillClass("neutral") },
  closed: { csvLabel: "Closed", className: pillClass("neutral") },
};

function getStatusKey(purchase: Purchase) {
  if (purchase.voided) return "voided";
  if (purchase.status) return purchase.status.toLowerCase().trim();
  return "";
}

// More useful than alphabetical
const statusRank: Record<string, number> = {
  overdue: 0,
  unpaid: 1,
  open: 1,
  partial: 2,
  partially_paid: 2,
  paid: 3,
  closed: 3,
  voided: 9,
};
function statusSortRank(p: Purchase): number {
  const k = getStatusKey(p);
  return statusRank[k] ?? 5;
}

function SourceDocumentIndicator() {
  const { t } = useTranslation();
  return (
    <span
      className="inline-flex h-6 w-6 items-center justify-center rounded-full text-gray-400"
      title={t("purchases.list.attached")}
      aria-label={t("purchases.list.attached")}
    >
      <PaperClipIcon className="h-4 w-4" aria-hidden="true" />
    </span>
  );
}

function downloadCsv(filename: string, rows: string[][]) {
  const csv = rows
    .map((r) =>
      r
        .map((cell) => {
          const v = cell ?? "";
          const needsQuotes = /[",\n]/.test(v);
          const escaped = String(v).replace(/"/g, '""');
          return needsQuotes ? `"${escaped}"` : escaped;
        })
        .join(","),
    )
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const ROWS_PER_PAGE = 10;

export default function PurchaseList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { company_id } = useParams<{ company_id: string }>();

  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<"loadList" | null>(null);

  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [page, setPage] = useState(1);

  const [sortKey, setSortKey] = useState<PurchaseSortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const fetchPurchases = useCallback(async () => {
    if (!company_id) return;
    setLoading(true);
    setError(null);
    try {
      // cache-buster avoids intermediary caches
      const data = await apiGet<Purchase[]>(
        `/purchases?company_id=${company_id}&_=${Date.now()}`,
      );
      setPurchases(data || []);
    } catch {
      setError("loadList");
    } finally {
      setLoading(false);
    }
  }, [company_id]);

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases, location.key, location.search]);

  // reset paging on filter changes
  useEffect(() => {
    setPage(1);
  }, [search, fromDate, toDate, purchases.length]);

  // reset paging on sort changes
  useEffect(() => {
    setPage(1);
  }, [sortKey, sortDir]);

  const handleHeaderSort = useCallback(
    (key: PurchaseSortKey) => {
      if (sortKey === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        // date default desc, strings default asc
        setSortDir(key === "date" ? "desc" : "asc");
      }
    },
    [sortKey],
  );

  const filteredPurchases = useMemo(() => {
    const q = search.toLowerCase().trim();
    return purchases.filter((p) => {
      const d = toISODateOnly(p.date);
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;

      if (!q) return true;

      const productsStr = getProductNames(p).join(", ").toLowerCase();
      const dueStr = (
        p.due_date ? toISODateOnly(String(p.due_date)) : ""
      ).toLowerCase();

      return (
        (p.invoice_number || "").toLowerCase().includes(q) ||
        String(p.invoice_id || "")
          .toLowerCase()
          .includes(q) ||
        (p.vendor?.name || "").toLowerCase().includes(q) ||
        (p.status || "").toLowerCase().includes(q) ||
        (p.date || "").toLowerCase().includes(q) ||
        dueStr.includes(q) ||
        String(p.amount).includes(q) ||
        productsStr.includes(q)
      );
    });
  }, [purchases, search, fromDate, toDate]);

  const sortedPurchases = useMemo(() => {
    const list = filteredPurchases.slice();

    list.sort((a, b) => {
      if (sortKey === "date") {
        const dateCmp = compareDates(a.date, b.date, sortDir);
        if (dateCmp !== 0) return dateCmp;

        const invCmp = compareInvoiceNo(invoiceStr(a), invoiceStr(b), "desc");
        if (invCmp !== 0) return invCmp;

        return sortDir === "desc" ? b.id - a.id : a.id - b.id;
      }

      if (sortKey === "products") {
        const pCmp = compareText(
          productsSortKey(a),
          productsSortKey(b),
          sortDir,
        );
        if (pCmp !== 0) return pCmp;

        const dateCmp = compareDates(a.date, b.date, "desc");
        if (dateCmp !== 0) return dateCmp;

        const invCmp = compareInvoiceNo(invoiceStr(a), invoiceStr(b), "desc");
        if (invCmp !== 0) return invCmp;

        return b.id - a.id;
      }

      if (sortKey === "status") {
        const ra = statusSortRank(a);
        const rb = statusSortRank(b);
        if (ra !== rb) return sortDir === "asc" ? ra - rb : rb - ra;

        const dateCmp = compareDates(a.date, b.date, "desc");
        if (dateCmp !== 0) return dateCmp;

        const invCmp = compareInvoiceNo(invoiceStr(a), invoiceStr(b), "desc");
        if (invCmp !== 0) return invCmp;

        return b.id - a.id;
      }

      // BaseSortKey: invoice
      const invCmp = compareInvoiceNo(invoiceStr(a), invoiceStr(b), sortDir);
      if (invCmp !== 0) return invCmp;

      const dateCmp = compareDates(a.date, b.date, "desc");
      if (dateCmp !== 0) return dateCmp;

      return b.id - a.id;
    });

    return list;
  }, [filteredPurchases, sortKey, sortDir]);

  const totalFiltered = sortedPurchases.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / ROWS_PER_PAGE));
  const pagedPurchases = sortedPurchases.slice(
    (page - 1) * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE,
  );
  const fromRow = totalFiltered === 0 ? 0 : (page - 1) * ROWS_PER_PAGE + 1;
  const toRow = Math.min(page * ROWS_PER_PAGE, totalFiltered);

  const selectValue = `${sortKey}:${sortDir}`;
  const handleSelect = (v: string) => {
    const [k, d] = v.split(":") as [PurchaseSortKey, SortDir];
    setSortKey(k);
    setSortDir(d);
  };

  const sortOptions: SortOption[] = [
    { value: "date:desc", label: t("purchases.list.dateNewest") },
    { value: "date:asc", label: t("purchases.list.dateOldest") },
    { value: "invoice:desc", label: t("purchases.list.invoiceHigh") },
    { value: "invoice:asc", label: t("purchases.list.invoiceLow") },
    { value: "products:asc", label: t("purchases.list.productsAsc") },
    { value: "products:desc", label: t("purchases.list.productsDesc") },
    { value: "status:asc", label: t("purchases.list.statusAsc") },
    { value: "status:desc", label: t("purchases.list.statusDesc") },
  ];

  // Localize only the display projection; status identity, rank/search and CSV
  // continue to use their original semantic values and fixed-English labels.
  const statusDisplay = (key: string, rawStatus?: string) => {
    switch (key) {
      case "paid": return t("purchases.status.paid");
      case "partial":
      case "partially_paid": return t("purchases.status.partialPaid");
      case "open":
      case "unpaid": return t("purchases.status.unpaid");
      case "overdue": return t("purchases.status.overdue");
      case "voided": return t("purchases.status.voided");
      case "closed": return t("purchases.status.closed");
      default: return rawStatus || "—";
    }
  };

  const newItems: MenuItem[] = [
    {
      label: t("purchases.common.new"),
      onClick: () => navigate(companyRoute(company_id, "/purchases/new")),
    },
  ];

  const moreItems: MenuItem[] = [
    {
      label: t("directory.common.exportCsv"),
      onClick: () => {
        const rows: string[][] = [
          [
            "Date",
            "Due Date",
            "Supplier Inv",
            "Vendor",
            "Products",
            "Amount",
            "Status",
          ],
          ...sortedPurchases.map((p) => [
            toISODateOnly(p.date),
            isOnAccount(p) ? toISODateOnly(String(p.due_date || "")) : "",
            p.invoice_number ||
              (p.invoice_id != null ? String(p.invoice_id) : ""),
            p.vendor?.name || "",
            getProductNames(p).join(" | "),
            String(p.amount ?? ""),
            statusInfoByKey[getStatusKey(p)]?.csvLabel || p.status || "",
          ]),
        ];
        downloadCsv("purchases.csv", rows);
      },
      disabled: sortedPurchases.length === 0,
    },
    {
      label: t("directory.common.clearFilters"),
      onClick: () => {
        setSearch("");
        setFromDate("");
        setToDate("");
        setSortKey("date");
        setSortDir("desc");
      },
    },
  ];

  if (loading)
    return (
      <div className={`text-center py-6 ${A.body} text-gray-500`}>
        {t("purchases.list.loading")}
      </div>
    );

  if (error) return <EmptyState
    message={t("purchases.errors.loadList")}
    heading={t("directory.common.nothingHere")}
    backLabel={t("directory.common.goBack")}
  />;

  if (!purchases.length)
    return (
      <EmptyState
        message={t("purchases.list.empty")}
        heading={t("directory.common.nothingHere")}
        backLabel={t("directory.common.goBack")}
        showNewButton
        newButtonText={t("purchases.common.new")}
        newButtonPath={companyRoute(company_id, "/purchases/new")}
      />
    );

  return (
    <div
      className={`min-h-screen bg-gray-50 flex flex-col items-center pt-6 ${A.body}`}
    >
      {/* Header */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-3xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={`${A.iconBtn} flex items-center justify-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <h2 className={`${A.title} flex-1 text-center`}>{t("purchases.list.title")}</h2>

        <button
          type="button"
          onClick={() => navigate(companyRoute(company_id, "/"))}
          className={`${A.iconBtn} flex items-center justify-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>

      {/* Standard toolbar (SalesList-style) */}
      <ListToolbar
        moreLabel={t("directory.common.more")}
        sortLabel={t("directory.common.sort")}
        fromLabel={t("sales.shared.from")}
        toLabel={t("sales.shared.to")}
        openCalendarLabel={t("sales.shared.openCalendar")}
        search={search}
        onSearchChange={setSearch}
        sortValue={selectValue}
        sortOptions={sortOptions}
        onSortChange={handleSelect}
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={setFromDate}
        onToDateChange={setToDate}
        newItems={newItems}
        moreItems={moreItems}
        maxWidthClass="max-w-5xl"
        searchPlaceholder={t("purchases.list.search")}
      />

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-4 w-full max-w-5xl px-2">
        {pagedPurchases.map((purchase) => {
          const statusKey = getStatusKey(purchase);
          const statusInfo = statusInfoByKey[statusKey];

          return (
            <div
              key={purchase.id}
              className={`${A.card} p-4 mb-2 cursor-pointer transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
              onClick={() =>
                navigate(companyRoute(company_id, `/purchases/${purchase.id}`))
              }
              tabIndex={0}
              aria-label={t("purchases.list.view", { id: purchase.id })}
              style={{
                minHeight: 120,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <div className="flex justify-between items-center mb-1">
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="text-[13px] font-medium text-gray-700">
                    {formatDate(purchase.date)}
                  </span>
                  {purchase.source_document && <SourceDocumentIndicator />}
                </span>

                <span className={statusInfo?.className || pillClass("neutral")}>
                  {statusDisplay(statusKey, purchase.status)}
                </span>
              </div>

              {isOnAccount(purchase) && (
                <div className={`${A.label} mb-1`}>
                  <span className="text-gray-500">{t("purchases.list.duePrefix")}{" "}</span>
                  <span className="text-gray-700">
                    {dueDateDisplay(purchase)}
                  </span>
                </div>
              )}

              <div className="flex justify-between items-center gap-3">
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-gray-700 truncate">
                    {purchase.vendor?.name || "—"}
                  </div>
                  {purchase.vendor?.email && (
                    <div className={A.label + " mt-0.5 truncate"}>
                      {purchase.vendor.email}
                    </div>
                  )}
                </div>

                <div className="text-right">
                  <div className={`${A.title} ${A.num} text-gray-900`}>
                    {formatAmount(purchase.amount)}
                  </div>
                </div>
              </div>

              <div className={`mt-2 ${A.label}`}>
                <span className="text-gray-500">{t("purchases.list.productsPrefix")}{" "}</span>
                <span className="text-gray-700">
                  {formatProductSummary(purchase)}
                </span>
              </div>

              <div
                className={`flex justify-between items-center mt-2 gap-2 ${A.label}`}
              >
                <div className="flex gap-2 items-center min-w-0">
                  <span className="text-gray-500">
                    {t("purchases.list.supplierPrefix")}
                  </span>
                  <span className="text-appleblue truncate">
                    {purchase.invoice_number || purchase.invoice_id || "—"}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination (mobile) */}
      <div
        className={`flex md:hidden justify-between items-center mt-4 w-full max-w-5xl px-2 ${A.label}`}
      >
        <span className="text-gray-500 select-none">
          {t("directory.common.showing", { from: fromRow, to: toRow, total: totalFiltered })}
        </span>

        <div className="flex gap-2">
          <Button
            variant="tertiary"
            minWidth="0px"
            className="px-2 active:scale-100"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            type="button"
          >
            {t("directory.common.prev")}
          </Button>

          <Button
            variant="tertiary"
            minWidth="0px"
            className="px-2 active:scale-100"
            disabled={page === totalPages || totalPages === 0}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            type="button"
          >
            {t("directory.common.next")}
          </Button>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block w-full max-w-5xl mx-auto">
        <ListTableShell>
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-gray-100">
                {/* Date (sortable) */}
                <th
                  className={`py-2 px-4 text-left ${A.label} select-none cursor-pointer`}
                  title={t("purchases.list.sortDate")}
                  onClick={() => handleHeaderSort("date")}
                >
                  {t("sales.common.date")}{" "}
                  {sortKey === "date" && (
                    <span className="text-gray-400">
                      {sortDir === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </th>

                {/* Due Date (display only) */}
                <th className={`py-2 px-2 text-left ${A.label}`}>
                  {t("purchases.common.dueShort")}
                </th>

                <th className={`py-2 px-2 text-left ${A.label}`}>
                  {t("purchases.list.invoiceNumber")}
                </th>

                <th className={`py-2 px-2 text-left ${A.label}`}>
                  {t("directory.vendors.singular")}
                </th>

                {/* Products (sortable) */}
                <th
                  className={`py-2 px-2 text-left ${A.label} select-none cursor-pointer`}
                  title={t("purchases.list.sortProducts")}
                  onClick={() => handleHeaderSort("products")}
                >
                  {t("sales.common.products")}{" "}
                  {sortKey === "products" && (
                    <span className="text-gray-400">
                      {sortDir === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </th>

                <th className={`py-2 px-2 text-right ${A.label}`}>
                  {t("sales.common.amount")}
                </th>

                {/* Status (sortable) */}
                <th
                  className={`py-2 px-2 text-center ${A.label} select-none cursor-pointer`}
                  title={t("purchases.list.sortStatus")}
                  onClick={() => handleHeaderSort("status")}
                >
                  {t("sales.common.status")}{" "}
                  {sortKey === "status" && (
                    <span className="text-gray-400">
                      {sortDir === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </th>

                <th className="py-2 px-2 text-right" />
              </tr>
            </thead>

            <tbody>
              {pagedPurchases.map((purchase) => {
                const statusKey = getStatusKey(purchase);
                const statusInfo = statusInfoByKey[statusKey];

                return (
                  <tr
                    key={purchase.id}
                    className="border-b border-gray-50 cursor-pointer text-gray-700 transition hover:bg-gray-50 focus:bg-gray-50"
                    onClick={() =>
                      navigate(
                        companyRoute(company_id, `/purchases/${purchase.id}`),
                      )
                    }
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter")
                        navigate(
                          companyRoute(company_id, `/purchases/${purchase.id}`),
                        );
                    }}
                    style={{ outline: "none" }}
                    aria-label={t("purchases.list.view", { id: purchase.id })}
                  >
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center gap-1.5">
                        <span>{formatDate(purchase.date)}</span>
                        {purchase.source_document && <SourceDocumentIndicator />}
                      </span>
                    </td>

                    <td className="py-3 px-2">{dueDateDisplay(purchase)}</td>

                    <td className="py-3 px-2">
                      {purchase.invoice_number || purchase.invoice_id || "—"}
                    </td>

                    <td className="py-3 px-2">
                      <div className="text-gray-700 font-medium">
                        {purchase.vendor?.name || "—"}
                      </div>
                      {purchase.vendor?.email && (
                        <div className={A.label + " mt-0.5"}>
                          {purchase.vendor.email}
                        </div>
                      )}
                    </td>

                    <td className="py-3 px-2">
                      <span className="line-clamp-2">
                        {formatProductSummary(purchase)}
                      </span>
                    </td>

                    <td
                      className={`py-3 px-2 text-right font-medium ${A.num} text-gray-900`}
                    >
                      {formatAmount(purchase.amount)}
                    </td>

                    <td className="py-3 px-2 text-center">
                      <span
                        className={
                          statusInfo?.className || pillClass("neutral")
                        }
                      >
                        {statusDisplay(statusKey, purchase.status)}
                      </span>
                    </td>

                    <td className="py-3 px-2 text-right">
                      <span className="text-gray-300 select-none">≡</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ListTableShell>

        {/* Pagination (desktop) */}
        <div
          className={`flex justify-between items-center mt-2 px-1 ${A.label}`}
        >
          <span className="text-gray-500 select-none">
            {t("directory.common.showing", { from: fromRow, to: toRow, total: totalFiltered })}
          </span>

          <div className="flex gap-2">
            <Button
              variant="tertiary"
              minWidth="0px"
              className="px-2 active:scale-100"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              type="button"
            >
              {t("directory.common.prev")}
            </Button>

            <Button
              variant="tertiary"
              minWidth="0px"
              className="px-2 active:scale-100"
              disabled={page === totalPages || totalPages === 0}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              type="button"
            >
              {t("directory.common.next")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
